import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { X, Mail, Loader2, CheckCircle, Calendar, Target, Zap, BarChart3, Download } from 'lucide-react';

export const PlanModal = ({ isOpen, onClose, planData, isLoading, onDownloadPdf, onSendEmail, readingData }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Trigger both download and email simultaneously
      await Promise.all([
        onDownloadPdf(),
        onSendEmail(email),
      ]);
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#0a1628]/95 backdrop-blur-md"
        onClick={onClose}
        data-testid="plan-modal-backdrop"
      />
      
      {/* Modal */}
      <div className={cn(
        "relative w-full max-w-3xl max-h-[90vh] overflow-y-auto",
        "bg-gradient-to-br from-[#1e2a4a] to-[#141c33]",
        "border-2 border-[#e89b47]/40 rounded-2xl",
        "shadow-2xl shadow-[#e89b47]/20"
      )} data-testid="plan-modal">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#d4c4a8]/60 hover:text-[#d4c4a8] transition-colors z-10"
          data-testid="plan-modal-close"
        >
          <X className="w-6 h-6"/>
        </button>

        {/* Header */}
        <div className="h-2 bg-gradient-to-r from-[#e89b47] via-[#d4883a] to-[#e89b47]" />
        
        <div className="p-8">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center",
                "bg-gradient-to-br from-[#e89b47]/20 to-[#e89b47]/5",
                "border-2 border-[#e89b47]/40"
              )}>
                <Calendar className="w-8 h-8 text-[#e89b47]" />
              </div>
            </div>
            <h2 className="font-display text-2xl text-[#e8dcc8] mb-2" data-testid="plan-modal-title">
              Your 90-Day Visibility Plan
            </h2>
            <p className="font-body text-[#d4c4a8]/70 text-sm">
              A personalized marketing roadmap based on your reading
            </p>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-[#e89b47] animate-spin mb-4" />
              <p className="font-display text-[#e8dcc8]">Crafting your plan...</p>
              <p className="font-body text-[#d4c4a8]/60 text-sm">This may take a moment</p>
            </div>
          ) : planData ? (
            <>
              {/* Plan Preview */}
              <div className="space-y-6 mb-8">
                {/* Phase cards */}
                {['phase1', 'phase2', 'phase3'].map((phaseKey, index) => {
                  const phase = planData[phaseKey];
                  if (!phase) return null;
                  
                  const icons = [Target, Zap, BarChart3];
                  const Icon = icons[index];
                  
                  return (
                    <div 
                      key={phaseKey}
                      className={cn(
                        "p-5 rounded-xl",
                        "bg-[#0a1628]/50 border border-[#d4c4a8]/20"
                      )}
                      data-testid={`plan-${phaseKey}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center",
                          "bg-[#e89b47]/20 border border-[#e89b47]/40"
                        )}>
                          <Icon className="w-5 h-5 text-[#e89b47]" />
                        </div>
                        <h3 className="font-display text-lg text-[#e8dcc8]">
                          {phase.name}
                        </h3>
                      </div>
                      
                      <p className="font-body text-[#d4c4a8]/70 text-sm mb-3">
                        <span className="text-[#e89b47]">Focus:</span> {phase.focus}
                      </p>
                      
                      <ul className="space-y-1 mb-3">
                        {phase.tasks?.slice(0, 2).map((task, i) => (
                          <li key={i} className="font-body text-[#d4c4a8]/80 text-sm flex items-start gap-2">
                            <span className="text-[#e89b47]">•</span>
                            {task}
                          </li>
                        ))}
                        {phase.tasks?.length > 2 && (
                          <li className="font-body text-[#d4c4a8]/50 text-sm italic">
                            + {phase.tasks.length - 2} more tasks in full PDF
                          </li>
                        )}
                      </ul>
                      
                      <p className="font-body text-[#e89b47] text-sm">
                        Milestone: {phase.milestone}
                      </p>
                    </div>
                  );
                })}

                {/* Quick wins preview */}
                {planData.quick_wins && (
                  <div className="p-4 rounded-xl bg-[#e89b47]/10 border border-[#e89b47]/30" data-testid="plan-quick-wins">
                    <h4 className="font-display text-[#e89b47] mb-2">Quick Wins (First 7 Days)</h4>
                    <ul className="space-y-1">
                      {planData.quick_wins.map((win, i) => (
                        <li key={`${i}-${win}`} className="font-body text-[#d4c4a8]/80 text-sm">• {win}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Email + Download Form */}
              {!isSubmitted ? (
                <div className={cn(
                  "p-6 rounded-xl",
                  "bg-gradient-to-r from-[#e89b47]/10 to-[#d4883a]/10",
                  "border-2 border-[#e89b47]/30"
                )} data-testid="plan-action-section">
                  <h3 className="font-display text-[#e8dcc8] mb-2 text-center">
                    Download &amp; Email Your Full Plan
                  </h3>
                  <p className="font-body text-[#d4c4a8]/60 text-sm text-center mb-4">
                    Enter your email to download the PDF and receive a copy in your inbox
                  </p>
                  
                  <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#d4c4a8]/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className={cn(
                          "w-full pl-10 pr-4 py-3 rounded-xl",
                          "bg-[#0a1628]/50 border-2 border-[#d4c4a8]/20",
                          "text-[#e8dcc8] placeholder-[#d4c4a8]/40",
                          "font-body text-sm",
                          "focus:outline-none focus:border-[#e89b47]/50"
                        )}
                        data-testid="plan-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#e89b47] text-[#0a1628] hover:bg-[#e89b47]/90 font-display px-6"
                      data-testid="plan-submit-btn"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <span className="flex items-center gap-2">
                          <Download className="w-4 h-4" />
                          Get My Plan
                        </span>
                      )}
                    </Button>
                  </form>
                  
                  {error && (
                    <p className="text-red-400 text-sm mt-2 text-center" data-testid="plan-error">{error}</p>
                  )}
                </div>
              ) : (
                <div className={cn(
                  "p-6 rounded-xl text-center",
                  "bg-green-500/10 border-2 border-green-500/30"
                )} data-testid="plan-success-section">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="font-display text-[#e8dcc8] mb-2">Plan Downloaded &amp; Sent!</h3>
                  <p className="font-body text-[#d4c4a8]/70 text-sm">
                    Your PDF has been downloaded. A copy was also sent to <span className="text-[#e89b47]">{email}</span>.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="font-body text-[#d4c4a8]/60">No plan data available</p>
            </div>
          )}
        </div>
        
        {/* Bottom decoration */}
        <div className="h-1 bg-gradient-to-r from-transparent via-[#e89b47]/50 to-transparent" />
      </div>
    </div>
  );
};

export default PlanModal;
